import type {
  IntegrationConnectionTestResponse,
  ZoomContactCenterIntegration,
  ZoomContactCenterIntegrationRequest,
} from "@repo/app-types/entities/integration";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { Input } from "@repo/ui/Inputs/input";
import { Link } from "@repo/ui/Link/index";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { IconEye, IconEyeOff } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { useState, type FC } from "react";
import { Dialog, DialogTrigger } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import {
  useConnectZoomContactCenterIntegrationMutation,
  useSetZoomContactCenterIntegrationEnabledMutation,
  useTestZoomContactCenterConnectionMutation,
  useUpdateZoomContactCenterIntegrationMutation,
} from "../../../api/entities/zoomContactCenterIntegration.js";
import { getApiErrorMessage } from "../../../api/helpers/errors.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";

const ZOOM_S2S_OAUTH_GUIDE_URL =
  "https://developers.zoom.us/docs/internal-apps/create/";

interface ZoomConnectionFormProps {
  mode: "connect" | "edit";
  layout?: "inline" | "modal";
  initialAccountId: string;
  initialClientId: string;
  isDisabled: boolean;
  onCancel?: () => void;
  onSave: (
    credentials: ZoomContactCenterIntegrationRequest,
  ) => Promise<boolean>;
}

/**
 * Credential entry, gated on a successful connection test.
 *
 * Saving is deliberately blocked until a test passes: credentials can be valid
 * while the OAuth app has none of the scopes the sync needs, and that failure is
 * otherwise silent — the sync looks connected and imports nothing.
 */
const ZoomConnectionForm: FC<ZoomConnectionFormProps> = (props) => {
  const {
    mode,
    layout = "inline",
    initialAccountId,
    initialClientId,
    isDisabled,
    onCancel,
    onSave,
  } = props;
  const isModalLayout = layout === "modal";

  const [accountId, setAccountId] = useState(initialAccountId);
  const [clientId, setClientId] = useState(initialClientId);
  const [clientSecret, setClientSecret] = useState("");
  const [isSecretVisible, setIsSecretVisible] = useState(false);
  const [hasAttemptedTest, setHasAttemptedTest] = useState(false);
  const [testResult, setTestResult] =
    useState<IntegrationConnectionTestResponse | null>(null);

  const [testConnection, testState] =
    useTestZoomContactCenterConnectionMutation();

  const trimmedAccountId = accountId.trim();
  const trimmedClientId = clientId.trim();
  const trimmedClientSecret = clientSecret.trim();
  const accountIdError =
    hasAttemptedTest && !trimmedAccountId ? "Enter an account ID." : undefined;
  const clientIdError =
    hasAttemptedTest && !trimmedClientId ? "Enter a client ID." : undefined;
  const clientSecretError =
    hasAttemptedTest && !trimmedClientSecret
      ? "Enter a client secret."
      : undefined;

  const credentials: ZoomContactCenterIntegrationRequest = {
    account_id: trimmedAccountId,
    client_id: trimmedClientId,
    client_secret: trimmedClientSecret,
  };

  // Any edit invalidates the previous result: it was about different credentials.
  const invalidateTest = () => {
    setTestResult(null);
  };

  const handleTest = async () => {
    setHasAttemptedTest(true);
    if (!trimmedAccountId || !trimmedClientId || !trimmedClientSecret) return;

    invalidateTest();
    try {
      setTestResult(await testConnection(credentials).unwrap());
    } catch (error) {
      setTestResult({
        status: "failure",
        error: getApiErrorMessage(
          error,
          "Couldn't reach Zoom — please try again.",
        ),
      });
    }
  };

  // A failed save surfaces through the parent's own error state.
  const handleSave = async () => {
    if (await onSave(credentials)) {
      setClientSecret("");
      setTestResult(null);
      setHasAttemptedTest(false);
    }
  };

  return (
    <div className="space-y-6">
      {mode === "connect" ? (
        <div className="space-y-2 text-sm leading-6 text-muted-foreground">
          <p>
            Create a Server-to-Server OAuth app in the{" "}
            <Link href={ZOOM_S2S_OAUTH_GUIDE_URL}>Zoom Marketplace</Link> and
            grant it the scope{" "}
            <code className="font-mono text-xs font-bold">
              contact_center_recording:read:admin
            </code>
            .
          </p>
          <p>
            The Account ID, Client ID, and Client Secret are on the app&apos;s
            App Credentials page.
          </p>
        </div>
      ) : null}

      <section>
        <div className="mb-5">
          {mode === "connect" ? (
            <h2 className="text-sm font-semibold text-foreground">
              Connection details
            </h2>
          ) : null}
          <p
            className={twMerge(
              "text-sm leading-5 text-muted-foreground",
              mode === "connect" && "mt-1",
            )}
          >
            Test the credentials before saving to confirm the required Zoom
            scopes are available.
          </p>
        </div>

        <div className={twMerge("space-y-5", !isModalLayout && "max-w-xl")}>
          <Input
            autoCapitalize="none"
            autoComplete="off"
            autoCorrect="off"
            errorDescription={accountIdError}
            isDisabled={isDisabled}
            label="Account ID"
            onChange={(event) => {
              setAccountId(event.target.value);
              invalidateTest();
            }}
            placeholder="Enter your Zoom account ID"
            required
            type="text"
            value={accountId}
          />

          <Input
            autoCapitalize="none"
            autoComplete="off"
            autoCorrect="off"
            errorDescription={clientIdError}
            isDisabled={isDisabled}
            label="Client ID"
            onChange={(event) => {
              setClientId(event.target.value);
              invalidateTest();
            }}
            placeholder="Enter your Zoom client ID"
            required
            type="text"
            value={clientId}
          />

          <Input
            autoCapitalize="none"
            autoComplete="new-password"
            autoCorrect="off"
            description={
              mode === "edit"
                ? "Re-enter the client secret to update this connection — Alpharun never sends a stored secret back."
                : undefined
            }
            errorDescription={clientSecretError}
            isDisabled={isDisabled}
            label="Client secret"
            onChange={(event) => {
              setClientSecret(event.target.value);
              invalidateTest();
            }}
            placeholder={
              mode === "edit"
                ? "Enter your client secret again"
                : "Enter your Zoom client secret"
            }
            required
            rightElement={
              <IconButton
                accessibleLabel={
                  isSecretVisible ? "Hide client secret" : "Show client secret"
                }
                icon={
                  isSecretVisible ? (
                    <IconEyeOff className="size-4" />
                  ) : (
                    <IconEye className="size-4" />
                  )
                }
                onClick={() => setIsSecretVisible(!isSecretVisible)}
                size={Size.SMALL}
                variant={ButtonVariant.Tertiary}
              />
            }
            type={isSecretVisible ? "text" : "password"}
            value={clientSecret}
          />
        </div>
      </section>

      {testResult ? (
        <div className={twMerge(!isModalLayout && "max-w-xl")}>
          {/* The message names what to change — a rejected credential and a
              missing scope need different fixes. */}
          <InfoBanner
            description={
              testResult.status === "failure" ? testResult.error : undefined
            }
            title={
              testResult.status === "success"
                ? "Connection successful"
                : "Couldn't connect to Zoom"
            }
            variant={
              testResult.status === "success"
                ? SimpleModalVariant.Standard
                : SimpleModalVariant.Warning
            }
          />
        </div>
      ) : null}

      <div
        className={twMerge(
          "flex flex-wrap items-center justify-end gap-2",
          isModalLayout ? "pt-1" : "max-w-xl border-t border-border-light pt-5",
        )}
      >
        {onCancel ? (
          <Button
            isDisabled={isDisabled}
            onClick={onCancel}
            variant={ButtonVariant.Tertiary}
          >
            Cancel
          </Button>
        ) : null}
        <Button
          isDisabled={isDisabled || testState.isLoading}
          isLoading={testState.isLoading}
          onClick={() => void handleTest()}
          variant={ButtonVariant.Secondary}
        >
          {testResult?.status === "success" ? "Test again" : "Test connection"}
        </Button>
        <Button
          isDisabled={isDisabled || testResult?.status !== "success"}
          onClick={() => void handleSave()}
          variant={ButtonVariant.Primary}
        >
          {mode === "connect" ? "Connect and start syncing" : "Save changes"}
        </Button>
      </div>
    </div>
  );
};

interface ZoomEditConnectionDialogProps {
  integration: ZoomContactCenterIntegration;
  isDisabled: boolean;
  onSave: (
    credentials: ZoomContactCenterIntegrationRequest,
  ) => Promise<boolean>;
}

const ZoomEditConnectionDialog: FC<ZoomEditConnectionDialogProps> = (props) => {
  const { integration, isDisabled, onSave } = props;

  return (
    <DialogTrigger>
      <Button
        className="shrink-0"
        isDisabled={isDisabled}
        size={Size.SMALL}
        variant={ButtonVariant.Secondary}
      >
        Edit connection
      </Button>
      <CenteredModal isDismissable size={Size.LARGE}>
        <Dialog className="px-4 pt-5 pb-4 text-left sm:p-6">
          {({ close }) => (
            <>
              <ModalHeader isCloseVisible>Edit connection</ModalHeader>
              <div className="mt-4">
                <ZoomConnectionForm
                  initialAccountId={integration.settings.account_id}
                  initialClientId={integration.settings.client_id}
                  isDisabled={isDisabled}
                  layout="modal"
                  mode="edit"
                  onCancel={close}
                  onSave={async (credentials) => {
                    const saved = await onSave(credentials);
                    if (saved) close();
                    return saved;
                  }}
                />
              </div>
            </>
          )}
        </Dialog>
      </CenteredModal>
    </DialogTrigger>
  );
};

export interface ZoomContactCenterIntegrationSettingsProps {
  integration: ZoomContactCenterIntegration | null;
  isLoading: boolean;
  isError: boolean;
  onReload: () => void;
  onRequestDisconnect: () => void;
}

export const ZoomContactCenterIntegrationSettings: FC<
  ZoomContactCenterIntegrationSettingsProps
> = (props) => {
  const { integration, isLoading, isError, onReload, onRequestDisconnect } =
    props;
  const [connectIntegration, connectState] =
    useConnectZoomContactCenterIntegrationMutation();
  const [updateIntegration, updateState] =
    useUpdateZoomContactCenterIntegrationMutation();
  const [setEnabled, setEnabledState] =
    useSetZoomContactCenterIntegrationEnabledMutation();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isPauseConfirmationOpen, setIsPauseConfirmationOpen] = useState(false);

  const isSaving = connectState.isLoading || updateState.isLoading;

  if (isLoading) {
    return <Loader style={LoaderStyle.SPINNER} />;
  }
  if (isError) {
    return (
      <div className="flex max-w-3xl flex-col items-start gap-3 rounded-lg border border-border-light bg-card p-4">
        <p className="text-sm text-muted-foreground">
          Couldn&apos;t load your Zoom Contact Center configuration.
        </p>
        <Button
          onClick={onReload}
          size={Size.SMALL}
          variant={ButtonVariant.Secondary}
        >
          Try again
        </Button>
      </div>
    );
  }

  /** Returns whether the write landed, so the form can clear the secret. */
  const saveConnection = async (
    credentials: ZoomContactCenterIntegrationRequest,
  ): Promise<boolean> => {
    setSaveError(null);
    try {
      if (integration) {
        await updateIntegration({
          integrationId: integration.id,
          credentials,
        }).unwrap();
      } else {
        await connectIntegration(credentials).unwrap();
      }
      return true;
    } catch (error) {
      setSaveError(
        getApiErrorMessage(
          error,
          "Could not save the Zoom Contact Center connection — please try again.",
        ),
      );
      return false;
    }
  };

  const changeEnabled = async (isEnabled: boolean) => {
    if (!integration) return;
    setSaveError(null);
    try {
      await setEnabled({
        integrationId: integration.id,
        isEnabled,
      }).unwrap();
    } catch (error) {
      setSaveError(
        getApiErrorMessage(
          error,
          `Could not ${isEnabled ? "resume" : "pause"} the Zoom sync — please try again.`,
        ),
      );
    }
  };

  if (!integration) {
    return (
      <Can
        I={PermissionActions.CREATE}
        a={PermissionSubjects.INTEGRATIONS}
        passThrough={true}
      >
        {(allowed) =>
          allowed ? (
            <>
              {saveError ? (
                <p className="mb-4 max-w-xl text-sm text-red-600">
                  {saveError}
                </p>
              ) : null}
              <ZoomConnectionForm
                initialAccountId=""
                initialClientId=""
                isDisabled={isSaving}
                mode="connect"
                onSave={saveConnection}
              />
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              You don&apos;t have permission to connect integrations.
            </p>
          )
        }
      </Can>
    );
  }

  const isLive = integration.settings.is_enabled;
  // The only timestamp we hold is the discovery high-water mark, so this says
  // what it actually is. There is no "last connected" time to show.
  const syncedThrough = integration.settings.last_recording_end_at
    ? DateTime.fromISO(
        integration.settings.last_recording_end_at,
      ).toLocaleString(DateTime.DATETIME_MED)
    : "Nothing synced yet";

  return (
    <div className="space-y-7">
      {saveError ? <p className="text-sm text-red-600">{saveError}</p> : null}

      <section className="rounded-lg border border-border-light bg-card p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className={twMerge(
                "mt-1.5 size-2.5 shrink-0 rounded-full",
                isLive ? "bg-success" : "bg-amber-400",
              )}
            />
            <div>
              <h2 className="text-sm font-semibold text-foreground">
                {isLive ? "Sync is live" : "Sync is paused"}
              </h2>
              <p className="mt-1 max-w-lg text-sm leading-5 text-muted-foreground">
                {isLive
                  ? "Alpharun checks Zoom Contact Center for new call recordings every 5 minutes."
                  : "No new call recordings are being imported. Resuming picks up from where the sync stopped — nothing recorded during the pause is lost."}
              </p>
            </div>
          </div>

          <Can
            I={PermissionActions.UPDATE}
            a={PermissionSubjects.INTEGRATIONS}
            passThrough={true}
          >
            {(allowed) => (
              <div className="shrink-0 pt-0.5">
                <Button
                  isDisabled={!allowed || setEnabledState.isLoading}
                  isLoading={setEnabledState.isLoading}
                  onClick={() => {
                    if (isLive) {
                      setIsPauseConfirmationOpen(true);
                      return;
                    }
                    void changeEnabled(true);
                  }}
                  size={Size.SMALL}
                  variant={
                    isLive ? ButtonVariant.Secondary : ButtonVariant.Primary
                  }
                >
                  {isLive ? "Pause sync" : "Resume sync"}
                </Button>
              </div>
            )}
          </Can>
        </div>

        <dl className="mt-5 grid gap-4 border-t border-border-light pt-4 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs font-medium text-muted-foreground">
              Sync schedule
            </dt>
            <dd className="mt-1 text-foreground">Every 5 minutes</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-muted-foreground">
              Synced through
            </dt>
            <dd className="mt-1 text-foreground">{syncedThrough}</dd>
          </div>
        </dl>
      </section>

      <section>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold text-foreground">
              Connection details
            </h2>
            <p className="mt-1 text-sm leading-5 text-muted-foreground">
              Credentials are used only to retrieve recordings from this Zoom
              account.
            </p>
          </div>
          <Can
            I={PermissionActions.UPDATE}
            a={PermissionSubjects.INTEGRATIONS}
            passThrough={true}
          >
            {(allowed) => (
              <ZoomEditConnectionDialog
                integration={integration}
                isDisabled={!allowed || isSaving}
                onSave={saveConnection}
              />
            )}
          </Can>
        </div>

        <dl className="mt-4 divide-y divide-border-light rounded-lg border border-border-light text-sm">
          <div className="grid gap-1 px-4 py-3 sm:grid-cols-[11rem_1fr] sm:gap-4">
            <dt className="text-muted-foreground">Account ID</dt>
            <dd className="min-w-0 truncate font-mono text-xs text-foreground">
              {integration.settings.account_id}
            </dd>
          </div>
          <div className="grid gap-1 px-4 py-3 sm:grid-cols-[11rem_1fr] sm:gap-4">
            <dt className="text-muted-foreground">Client ID</dt>
            <dd className="min-w-0 truncate font-mono text-xs text-foreground">
              {integration.settings.client_id}
            </dd>
          </div>
          <div className="grid gap-1 px-4 py-3 sm:grid-cols-[11rem_1fr] sm:gap-4">
            <dt className="text-muted-foreground">Client secret</dt>
            {/* A fixed mask, not the stored value: the secret is write-only and
                never leaves the server, so there is nothing here to reveal. */}
            <dd className="font-mono text-xs tracking-widest text-foreground">
              ••••••••••••••••
            </dd>
          </div>
        </dl>
      </section>

      <section className="border-t border-border-light pt-6">
        <h2 className="text-sm font-semibold text-foreground">
          How calls are handled
        </h2>
        <p className="mt-1 max-w-2xl text-sm leading-6 text-muted-foreground">
          Agents are matched to Alpharun teammates using their Zoom profile
          email. Existing playbook routing rules decide where imported calls are
          sent.
        </p>
      </section>

      <section className="flex flex-col gap-3 border-t border-border-light pt-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold text-foreground">
            Disconnect Zoom Contact Center
          </h2>
          <p className="mt-1 text-sm leading-5 text-muted-foreground">
            This stops new call recordings from syncing to Alpharun.
          </p>
        </div>
        <Can
          I={PermissionActions.DELETE}
          a={PermissionSubjects.INTEGRATIONS}
          passThrough={true}
        >
          {(allowed) => (
            <Button
              isDisabled={!allowed}
              onClick={onRequestDisconnect}
              size={Size.SMALL}
              variant={ButtonVariant.Warning}
            >
              Disconnect
            </Button>
          )}
        </Can>
      </section>

      <SimpleModal
        confirmButtonText="Pause sync"
        isOpen={isPauseConfirmationOpen}
        onCancel={() => setIsPauseConfirmationOpen(false)}
        onConfirm={() => {
          void changeEnabled(false);
          setIsPauseConfirmationOpen(false);
        }}
        // Resuming genuinely does backfill the paused stretch: the sync tracks
        // how far it has read, not the wall clock. It catches up in bounded
        // steps, so a long pause takes a while to work through the backlog.
        subtitle="Call recordings created while the sync is paused are imported when you resume, catching up gradually rather than all at once."
        title="Pause Zoom sync?"
        variant={SimpleModalVariant.Warning}
      />
    </div>
  );
};
